import { FC } from "react";
import { useFormContext } from "react-hook-form";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  SearchSaveFiltersSubHeader,
} from "../../../../components";
import { MAX_LENGTH_75 } from "../../../../helpers/constants";
import { UserRole } from "../../../../helpers/types/common";
import { setApiDataToDropDownOptionForRole } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { UsersSearchFormKey } from "../../modelsAndSchemas/models";
import { searchUsersSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { UsersSearchFormWrapper } from "./UsersSearchForm.style";

interface UsersSearchFormProps {
  pageId: string;
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
  disableActionBtn: boolean;
  rolesList?: UserRole[];
}

export const UsersSearchForm: FC<UsersSearchFormProps> = ({
  pageId,
  handleClearAllSearch,
  handleSearchClick,
  disableActionBtn,
  rolesList,
}) => {
  const {
    commonLabels,
    fetchComponentTitle,
    useFetchLabel,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();

  const { control } = useFormContext();

  const {
    searchUsers,
    roleLabel,
    serviceOffice,
    userEmailID,
    userName,
    userStatus,
    filterByList,
    statusList,
  } = searchUsersSiteCoreKeys;

  const getUserFormLabel = useFetchLabel(searchUsers);

  const renderUserFormHeader = () => {
    return (
      <SearchSaveFiltersSubHeader
        onSavedFilterClick={() => {}}
        onResetAllClick={handleClearAllSearch}
        isSavedFilterDisabled={disableActionBtn}
        resetButtonTestId={`${pageId}-clearAll`}
        savedFilterTestId={`${pageId}-savedFilter`}
      />
    );
  };

  return (
    <UsersSearchFormWrapper>
      <AccordionHeader
        title={<AccordionHeaderText label={fetchComponentTitle(searchUsers)} />}
      >
        {renderUserFormHeader()}
        <form data-testid="user-search-form">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={UsersSearchFormKey.userName}
              >
                <MuiSelectWithTextInput
                  options={fetchDropdownValues(searchUsers, filterByList)}
                  label={getUserFormLabel(userName)}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={UsersSearchFormKey.userNameFilter}
                  id={`${pageId}-${UsersSearchFormKey.userName}`}
                  selectBoxId={`${pageId}-${UsersSearchFormKey.userNameFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid={`${pageId}-${UsersSearchFormKey.userName}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={UsersSearchFormKey.userEmail}
              >
                <MuiSelectWithTextInput
                  options={fetchDropdownValues(searchUsers, filterByList)}
                  label={getUserFormLabel(userEmailID)}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={UsersSearchFormKey.userEmailFilter}
                  id={`${pageId}-${UsersSearchFormKey.userEmail}`}
                  selectBoxId={`${pageId}-${UsersSearchFormKey.userEmailFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid={`${pageId}-${UsersSearchFormKey.userEmail}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={UsersSearchFormKey.serviceOffice}
              >
                <MuiSelectWithTextInput
                  options={fetchDropdownValues(searchUsers, filterByList)}
                  label={getUserFormLabel(serviceOffice)}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={UsersSearchFormKey.serviceOfficeFilter}
                  id={`${pageId}-${UsersSearchFormKey.serviceOffice}`}
                  selectBoxId={`${pageId}-${UsersSearchFormKey.serviceOfficeFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid={`${pageId}-${UsersSearchFormKey.serviceOffice}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={UsersSearchFormKey.userStatus}
              >
                <MuiSelect
                  label={getUserFormLabel(userStatus)}
                  options={fetchDropdownValues(searchUsers, statusList)}
                  value=""
                  id={`${pageId}-${UsersSearchFormKey.userStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  visibleDefaultSelectOption
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController control={control} name={UsersSearchFormKey.role}>
                <MuiSelect
                  label={getUserFormLabel(roleLabel)}
                  options={setApiDataToDropDownOptionForRole(rolesList ?? [])}
                  value=""
                  id={`${pageId}-${UsersSearchFormKey.role}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  visibleDefaultSelectOption
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>

          <MuiGrid container className="button-container">
            <MuiButtonSecondary id={`${pageId}-saveFilter`}>
              {commonLabels.saveFilter}
            </MuiButtonSecondary>
            <MuiButtonPrimary
              onClick={handleSearchClick}
              id={`${pageId}-search`}
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </UsersSearchFormWrapper>
  );
};
